/**
 * Shared vocabulary for the top-level `reports` collection's *buckets*.
 *
 * The top-level `reports` collection is a set of buckets, keyed by course id (a
 * course bucket), the special `_uncategorized` id (the bulk bucket for
 * uncategorized inbound email), or the reserved `_agent` id (the email agent's
 * operational state). Each bucket holds an `emails` subcollection (see
 * `emailAgent/inboundEmail`); course buckets additionally hold an
 * `onPlatformReports` subcollection (see `report`).
 *
 * This module holds only the bucket-level path vocabulary that both the
 * email-agent schemas and the reports triage model depend on, so the two can be
 * consumed independently.
 */

/** Top-level collection holding per-bucket documents (course, bulk, or agent). */
export const REPORTS_COLLECTION = 'reports' as const;

/** Bucket id of the bulk bucket that holds uncategorized emails. */
export const UNCATEGORIZED_BUCKET_ID = '_uncategorized' as const;

/**
 * Reserved bucket id holding the email agent's operational state (audit logs,
 * poll watermark, gate config), consolidated under the `reports` tree rather
 * than as separate top-level collections. Not a course — but the generic
 * per-bucket rules still bind it: Firestore rules are additive, and the
 * `reports/{bucketId}` match in `firestore.rules` matches this id like any
 * other. Those rules grant nothing here only because this bucket's
 * subcollection names are disjoint from the course-bucket ones, so keep the
 * two sets disjoint.
 */
export const AGENT_BUCKET_ID = '_agent' as const;

/** Subcollection (under the `_agent` bucket) holding agent audit-log entries. */
export const AGENT_LOGS_SUBCOLLECTION = 'logs' as const;

/** Subcollection (under the `_agent` bucket) holding agent server-side state. */
export const AGENT_STATE_SUBCOLLECTION = 'state' as const;

/** Subcollection (under the `_agent` bucket) holding the agent gate config. */
export const AGENT_CONFIG_SUBCOLLECTION = 'config' as const;

/**
 * Subcollection (under the `_agent` bucket) holding auto-reply ack markers,
 * keyed by conversation (`threadId`). A marker's presence means the agent has
 * already sent an automatic acknowledgement for that thread in SOME bucket, so
 * the once-per-conversation guard is bucket-independent (unlike the per-bucket
 * thread doc). See `getAgentAckDocumentPath` and `maybeAutoReply` in
 * `processEmail`.
 */
export const AGENT_ACKS_SUBCOLLECTION = 'acks' as const;

/**
 * Subcollection (under the `_agent` bucket) holding the inquiry-type taxonomy —
 * the classifier's admin-editable category vocabulary. See
 * `emailAgent/inquiryType`.
 */
export const AGENT_INQUIRY_TYPES_SUBCOLLECTION = 'inquiryTypes' as const;

/**
 * Path to a bucket document.
 *
 * @param args.bucketId - A course id, {@link UNCATEGORIZED_BUCKET_ID} for the
 *   bulk bucket, or {@link AGENT_BUCKET_ID} for the agent-state bucket.
 * @returns Firestore doc path `reports/{bucketId}`.
 */
export function getBucketDocPath({bucketId}: {bucketId: string}): string {
  return `${REPORTS_COLLECTION}/${bucketId}`;
}

/**
 * Collection path for auto-reply ack markers, nested under the reserved
 * `_agent` bucket.
 *
 * @returns Firestore collection path `reports/_agent/acks`.
 * @throws Never — pure string construction.
 */
export function getAgentAcksCollectionPath(): string {
  return `${getBucketDocPath({bucketId: AGENT_BUCKET_ID})}/${AGENT_ACKS_SUBCOLLECTION}`;
}

/**
 * Path to a single auto-reply ack marker, keyed by conversation. The marker is
 * created transactionally the first time the agent auto-acknowledges a thread
 * (in any bucket); a pre-existing marker means the thread was already acked.
 *
 * @param args.threadId - The conversation id (used as the marker's document id).
 *   Callers must not pass an unthreaded email's `null` threadId — such messages
 *   are not ackable (there is no stable key to dedupe on).
 * @returns Firestore doc path `reports/_agent/acks/{threadId}`.
 * @throws Never — pure string construction.
 */
export function getAgentAckDocumentPath({
  threadId,
}: {
  threadId: string;
}): string {
  return `${getAgentAcksCollectionPath()}/${threadId}`;
}
